/**
 * UsersTable Component
 *
 * Displays a unified table of users and pending invitations.
 * Pending invitations are shown with a grayed-out row and "Pending" badge.
 */
import { useMemo, useState } from 'react';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { Avatar, Badge, Icon, Input, Select, StatusIndicator } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import type { InvitationData, TableRow, UserData } from './types';
import {
  getAvatarName,
  getRowClassName,
  getRowKey,
  getStatusConfig
} from './users-table/user-table-helpers';
import { UserRowActions } from './users-table/UserRowActions';
import { InvitationRowActions } from './users-table/InvitationRowActions';
import {
  getDisplayRoles,
  getSingleRoleBadgeColor,
  isSelectableRole,
  type SelectableRole
} from '@/features/admin/constants/roles';

export type { TableRow };

interface UsersTableProps {
  rows: TableRow[];
  loading: boolean;
  error?: unknown;
  currentUserId?: string;
  onEdit: (user: UserData) => void;
  onDeactivate: (user: UserData) => void;
  onReactivate: (user: UserData) => void;
  onDelete: (user: UserData) => void;
  onResendInvitation: (invitation: InvitationData) => void;
  onCancelInvitation: (invitation: InvitationData) => void;
}

export function UsersTable({
  rows,
  loading,
  error,
  currentUserId,
  onEdit,
  onDeactivate,
  onReactivate,
  onDelete,
  onResendInvitation,
  onCancelInvitation
}: UsersTableProps) {
  const t = useFeatureTranslation('systemSettings.users');
  const tRole = useEnumTranslation('userRole');
  const showLoading = useDelayedLoading(loading);
  const tStatus = useEnumTranslation('accountStatus');

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<SelectableRole | ''>('');
  const [statusFilter, setStatusFilter] = useState('');

  const uniqueRoles = useMemo(() => {
    const roles = new Set(rows.flatMap(row => getDisplayRoles(row.data.roles)));
    return Array.from(roles);
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(row => {
      const email = row.data.email.toLowerCase();
      const name = row.type === 'user' ? row.data.displayName.toLowerCase() : '';
      const matchesSearch = !q || email.includes(q) || name.includes(q);
      const matchesRole = !roleFilter || getDisplayRoles(row.data.roles).includes(roleFilter);
      const matchesStatus =
        !statusFilter ||
        (statusFilter === 'PENDING' && row.type === 'invitation') ||
        (statusFilter !== 'PENDING' && row.type === 'user' && row.data.status === statusFilter);
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [rows, search, roleFilter, statusFilter]);

  const statusLabels = {
    active: tStatus('ACTIVE'),
    deactivated: tStatus('DEACTIVATED'),
    pending: tStatus('PENDING'),
    fallback: (s: string) => tStatus(s)
  };

  const roleOptions = [
    { value: '', label: t('filter.allRoles') },
    ...uniqueRoles.map(role => ({ value: role, label: tRole(role) }))
  ];

  const statusOptions = [
    { value: '', label: t('filter.allStatuses') },
    { value: 'ACTIVE', label: tStatus('ACTIVE') },
    { value: 'DEACTIVATED', label: tStatus('DEACTIVATED') },
    { value: 'PENDING', label: tStatus('PENDING') }
  ];

  return (
    <div className='flex flex-col gap-md'>
      {/* Search & Filters */}
      <div className='flex flex-col gap-md md:flex-row md:items-center'>
        <div className='flex-1 min-w-0'>
          <Input
            type='text'
            inputMode='search'
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('filter.searchPlaceholder')}
            height='sm'
            className='h-auto! min-h-8 py-xs!'
            leftContent={<Icon name='magnifying-glass' size='sm' />}
          />
        </div>

        <div className='flex gap-md'>
          <div className='flex-1 md:flex-none md:min-w-45'>
            <Select
              value={roleFilter}
              onChange={value => {
                const v = String(value);
                setRoleFilter(isSelectableRole(v) ? v : '');
              }}
              options={roleOptions}
              height='sm'
              ariaLabel={t('filter.roleLabel')}
              UnselectedOption={undefined}
            />
          </div>

          <div className='flex-1 md:flex-none md:min-w-45'>
            <Select
              value={statusFilter}
              onChange={value => setStatusFilter(String(value))}
              options={statusOptions}
              height='sm'
              ariaLabel={t('filter.statusLabel')}
              UnselectedOption={undefined}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className='border border-border-base rounded-lg overflow-visible'>
        {loading && rows.length === 0 ? (
          showLoading ? (
            <div className='py-xl text-center'>
              <p className='text-sm text-foreground-muted'>{t('table.loading')}</p>
            </div>
          ) : null
        ) : error ? (
          <div className='py-xl text-center'>
            <p className='text-sm text-error-text'>{t('table.error')}</p>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className='py-xl text-center'>
            <p className='text-sm text-foreground-muted'>{t('table.empty')}</p>
          </div>
        ) : (
          <div>
            {filteredRows.map(row => {
              const { color: statusColor, label } = getStatusConfig(row, statusLabels);
              const isDeactivated = row.type === 'user' && row.data.status === 'DEACTIVATED';

              return (
                <div key={getRowKey(row)} className={getRowClassName(row)}>
                  {/* Avatar + Name/Email + Actions */}
                  <div className='flex items-center gap-md w-full md:contents'>
                    <Avatar name={getAvatarName(row)} size='sm' />

                    {/* Email + display name */}
                    <div className='flex-1 min-w-0'>
                      <p
                        className={`text-sm font-medium text-foreground truncate ${isDeactivated ? 'line-through' : ''}`}
                      >
                        {row.type === 'user' ? row.data.displayName : row.data.email}
                      </p>
                      {row.type === 'user' ? (
                        <p
                          className={`text-xs text-foreground-muted truncate ${isDeactivated ? 'line-through' : ''}`}
                        >
                          {row.data.email}
                          {row.data.id === currentUserId && (
                            <span className='ml-xs text-foreground-muted'>{t('table.you')}</span>
                          )}
                        </p>
                      ) : (
                        <p className='text-xs text-foreground-muted truncate md:hidden'>{label}</p>
                      )}
                    </div>

                    {/* Actions (⋮ dropdown) – mobile only */}
                    <div className='shrink-0 md:hidden'>
                      {row.type === 'invitation' ? (
                        <InvitationRowActions
                          invitation={row.data}
                          variant='dropdown'
                          onEdit={onEdit}
                          onResendInvitation={onResendInvitation}
                          onCancelInvitation={onCancelInvitation}
                        />
                      ) : (
                        <UserRowActions
                          user={row.data}
                          isCurrentUser={row.data.id === currentUserId}
                          variant='dropdown'
                          onEdit={onEdit}
                          onDeactivate={onDeactivate}
                          onReactivate={onReactivate}
                          onDelete={onDelete}
                        />
                      )}
                    </div>
                  </div>

                  {/* Role badge + Status (mobile: second row, wraps if needed) */}
                  <div className='flex flex-wrap items-center gap-x-sm gap-y-xs w-full pl-11 min-w-0 md:w-auto md:pl-0 md:flex-nowrap md:contents'>
                    <div className='shrink-0 md:w-60 md:flex md:justify-start md:flex-wrap md:gap-xs'>
                      {row.type === 'user' && row.data.isSystemUser ? (
                        <Badge color='neutral' size='sm'>
                          {t('table.systemUser')}
                        </Badge>
                      ) : (
                        getDisplayRoles(row.data.roles).map(role => (
                          <Badge key={role} color={getSingleRoleBadgeColor(role)} size='sm'>
                            {tRole(role)}
                          </Badge>
                        ))
                      )}
                    </div>

                    <div className='md:w-36 md:flex md:justify-start'>
                      <StatusIndicator color={statusColor} label={label} />
                    </div>
                  </div>

                  {/* Actions (inline icons) – desktop only */}
                  <div className='hidden md:block shrink-0'>
                    {row.type === 'invitation' ? (
                      <InvitationRowActions
                        invitation={row.data}
                        variant='inline'
                        onEdit={onEdit}
                        onResendInvitation={onResendInvitation}
                        onCancelInvitation={onCancelInvitation}
                      />
                    ) : (
                      <UserRowActions
                        user={row.data}
                        isCurrentUser={row.data.id === currentUserId}
                        variant='inline'
                        onEdit={onEdit}
                        onDeactivate={onDeactivate}
                        onReactivate={onReactivate}
                        onDelete={onDelete}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
